import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  taxApi, elsterApi,
  type WizardCatalog, type WizardStep, type WizardField,
  type Hnk35aPosition, type KrankheitsResult, type KryptoResult, type VastResult, type ElsterCertificate,
  type EstPrognose,
} from '../../../lib/api';
import { Button, Card, ErrorNote, euro } from '../../ui';
import { ElsterSubmit } from '../elster/SubmitFlow';

interface Props {
  companyId: string;
  year: number;
  onClose: () => void;
}

type Answers = Record<string, string>;

// ---------- helper: money field (EUR string <-> cents integer stored as string) ----------

function centsToEurStr(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function eurStrToCents(s: string): number {
  const normalized = s.replace(/\./g, '').replace(',', '.').trim();
  const val = parseFloat(normalized);
  if (isNaN(val)) return 0;
  return Math.round(val * 100);
}

/**
 * Schluessel aus frueheren Abrufen -> heutige Feldnamen.
 *
 * Die abgerufenen Bescheinigungen werden im Entwurf gespeichert. Belege, die
 * vor der Korrektur der Parser-Schluessel geholt wurden, tragen noch die alten
 * Namen — ohne diese Zuordnung bliebe die Anlage Vorsorgeaufwand leer, bis der
 * Nutzer neu abruft. Kann entfallen, wenn keine Altentwuerfe mehr im Umlauf
 * sind.
 */
const VAST_ALTSCHLUESSEL: Record<string, string> = {
  rvBeitrag: 'rvArbeitnehmer',
  kvBeitrag: 'kvArbeitnehmer',
  pvBeitrag: 'pvArbeitnehmer',
  kvBasis: 'kvSelbst',
};

/**
 * Antwort so darstellen, wie ein Mensch sie liest.
 *
 * In der Zusammenfassung standen sonst Rohwerte: "02" statt "Evangelisch",
 * "true" statt "Ja", "2000-10-01" statt "01.10.2000". Gerade bei
 * Auswahlfeldern ist der Schluessel nicht nachpruefbar — man sieht nicht, ob
 * die richtige Konfession gewaehlt wurde.
 */
function anzeigeWert(field: WizardField, raw: string): string {
  if (raw === '') return '';
  if (field.type === 'money' || field.type === 'moneyEur') return euro(eurStrToCents(raw));
  if (field.type === 'bool' || field.type === 'check') return raw === 'true' ? 'Ja' : 'Nein';
  if (field.type === 'select') {
    return field.options?.find((o) => o.value === raw)?.label ?? raw;
  }
  if (field.type === 'date') {
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : raw;
  }
  return raw;
}

/**
 * Zeile im amtlichen Vordruck, rechts im Eingabefeld.
 *
 * Bescheinigungen und Papiervordruck nennen die Zeile ("Zeile 16: Gezahlte
 * Beiträge zur Krankenversicherung") — die Nummer daneben erspart das Suchen.
 * Bewusst dezent: eine Orientierungshilfe, kein Bedienelement.
 */
function Zeilennummer({ zeile }: { zeile: string | undefined }): ReactElement | null {
  if (!zeile) return null;
  return (
    <span
      title={`Zeile ${zeile} im amtlichen Vordruck`}
      className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-gray-500 dark:bg-gray-700/70 dark:text-gray-400"
    >
      Z{zeile}
    </span>
  );
}

// ---------- single field renderer ----------

function WizardFieldInput({
  field,
  value,
  onChange,
  answers,
  ausBescheinigung,
}: {
  field: WizardField;
  value: string;
  onChange: (v: string) => void;
  answers: Answers;
  /** Wert stammt aus einer Bescheinigung des Finanzamts. */
  ausBescheinigung?: boolean;
}): ReactElement | null {
  // showIf: entweder "key=value" oder nur "key" (dann muss das bool-Feld
  // angehakt sein). Ohne die Kurzform muesste jedes Schaltfeld "=true"
  // mitschleppen.
  if (field.showIf) {
    const [k, v] = field.showIf.split('=');
    if (k !== undefined) {
      const expected = v ?? 'true';
      if ((answers[k] ?? '') !== expected) return null;
    }
  }

  const inputCls =
    'w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';

  // 'check' (Ankreuzfeld) und 'bool' (Ja/Nein) sehen gleich aus — der
  // Unterschied liegt nur in der ELSTER-Kodierung (X bzw. 1).
  if (field.type === 'bool' || field.type === 'check') {
    return (
      <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-3 text-sm dark:border-gray-700">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-blue-600"
        />
        <span className="text-gray-700 dark:text-gray-300">
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </span>
        {field.help && <span className="ml-auto text-xs text-gray-400">{field.help}</span>}
      </label>
    );
  }

  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {field.label}
        {field.required && <span className="text-red-500"> *</span>}
        {ausBescheinigung && (
          <span
            title="Aus Bescheinigung des Finanzamts übernommen"
            className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
          >
            B
          </span>
        )}
      </span>
      {field.type === 'money' || field.type === 'moneyEur' ? (
        <div className="relative">
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            // moneyEur wird in vollen Euro uebermittelt (Schema: Ganzzahl).
            placeholder={field.type === 'moneyEur' ? '0' : '0,00'}
            // Platz rechts fuer "EUR" und ggf. die Zeilennummer freihalten,
            // sonst schiebt sich der Betrag darunter.
            className={`${inputCls} ${field.zeile ? 'pr-28' : 'pr-14'}`}
          />
          <span className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2 text-sm text-gray-400">
            EUR
            <Zeilennummer zeile={field.zeile} />
          </span>
        </div>
      ) : field.type === 'number' ? (
        <div className="relative">
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} ${field.zeile ? 'pr-20' : ''}`}
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2">
            <Zeilennummer zeile={field.zeile} />
          </span>
        </div>
      ) : field.type === 'date' ? (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      ) : field.type === 'select' ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
          <option value="">— bitte wählen —</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} ${field.zeile ? 'pr-20' : ''}`}
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2">
            <Zeilennummer zeile={field.zeile} />
          </span>
        </div>
      )}
      {field.help && (
        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{field.help}</span>
      )}
    </label>
  );
}

// ---------- Wiederholbare Anlagen (mehrere Kinder / Mietobjekte) ----------

/**
 * ELSTER verlangt je Kind bzw. je Mietobjekt eine EIGENE Anlage. Dieser Block
 * rendert beliebig viele Eintraege; die Antworten liegen unter
 * "<stepKey>.<n>.<feldKey>" und werden im Backend mit Wiederholungs-Index
 * gemappt.
 */
function RepeatableStep({
  step, answers, setAnswer, clearAnswer,
}: {
  step: WizardStep;
  answers: Answers;
  setAnswer: (key: string, value: string) => void;
  clearAnswer: (key: string) => void;
}): ReactElement {
  const rep = step.repeatable!;
  // Wieviele Eintraege sind belegt? Mindestens einer wird immer gezeigt.
  const used = Math.max(
    1,
    ...step.fields.flatMap((f) =>
      Array.from({ length: rep.max }, (_, n) => (answers[`${step.key}.${n}.${f.key}`] ? n + 1 : 0)),
    ),
  );
  const [count, setCount] = useState(used);

  function removeAt(idx: number) {
    // Nachfolgende Eintraege aufruecken, damit keine Luecke entsteht.
    for (let n = idx; n < count - 1; n++) {
      for (const f of step.fields) {
        const next = answers[`${step.key}.${n + 1}.${f.key}`] ?? '';
        setAnswer(`${step.key}.${n}.${f.key}`, next);
      }
    }
    for (const f of step.fields) clearAnswer(`${step.key}.${count - 1}.${f.key}`);
    setCount((c) => Math.max(1, c - 1));
  }

  return (
    <div className="space-y-4">
      {Array.from({ length: count }, (_, n) => (
        <div key={n} className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {rep.itemLabel} {n + 1}
            </span>
            {count > 1 && (
              <button
                onClick={() => removeAt(n)}
                className="text-xs text-gray-500 hover:underline dark:text-gray-400"
              >
                entfernen
              </button>
            )}
          </div>
          <div className="space-y-4">
            {step.fields.map((f) => (
              <WizardFieldInput
                key={f.key}
                field={f}
                value={answers[`${step.key}.${n}.${f.key}`] ?? ''}
                onChange={(v) => setAnswer(`${step.key}.${n}.${f.key}`, v)}
                answers={answers}
              />
            ))}
          </div>
        </div>
      ))}
      {count < rep.max && (
        <Button variant="secondary" onClick={() => setCount((c) => c + 1)}>
          + {rep.addLabel}
        </Button>
      )}
    </div>
  );
}

// ---------- Vorausgefüllte Steuererklärung (VaSt-Belegabruf) ----------

/**
 * Ruft die beim Finanzamt gespeicherten Belege ab (Lohnsteuerbescheinigung,
 * Renten, Versicherungsbeiträge). Der Abruf laeuft mit dem EIGENEN Zertifikat
 * des Nutzers — deshalb Zertifikatswahl + PIN wie beim Uebermitteln.
 *
 * Die Belege werden nicht gespeichert; angezeigt werden nur die ausgelesenen
 * Werte zum Abgleich.
 */
function VastFetchBox({
  companyId, year, onUebernehmen, gespeichert, abrufAm, onAbgerufen,
}: {
  companyId: string;
  year: number;
  /** Uebernimmt die abgerufenen Werte in die Wizard-Felder. */
  /** Uebernimmt die Werte und liefert die Zahl der gefuellten Felder. */
  onUebernehmen: (werte: { key: string; cents?: number; text?: string }[]) => number;
  /** Belege aus dem gespeicherten Entwurf (erspart einen erneuten Abruf). */
  gespeichert?: VastResult['belege'] | null;
  abrufAm?: string | null;
  /** Meldet einen frischen Abruf nach oben, damit er im Entwurf landet. */
  onAbgerufen: (belege: VastResult['belege']) => void;
}): ReactElement {
  // Liegen gespeicherte Belege vor, direkt das Ergebnis zeigen statt des
  // Abrufdialogs — sonst wirkt es, als waere nie etwas abgerufen worden.
  const [open, setOpen] = useState(Boolean(gespeichert?.length));
  const [certs, setCerts] = useState<ElsterCertificate[]>([]);
  const [certId, setCertId] = useState('');
  const [pin, setPin] = useState('');
  const [idnr, setIdnr] = useState('');
  const [needIdnr, setNeedIdnr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [frisch, setFrisch] = useState<VastResult | null>(null);
  /** Anzahl tatsaechlich gefuellter Felder; null = noch nicht uebernommen. */
  const [uebernommen, setUebernommen] = useState<number | null>(null);
  /** Nutzer will trotz gespeicherter Belege neu abrufen -> Formular zeigen. */
  const [neuAbrufen, setNeuAbrufen] = useState(false);

  // Frischer Abruf schlaegt den gespeicherten Stand. Der Entwurf laedt
  // asynchron, deshalb abgeleitet statt in useState (das liefe nur einmal).
  const result: VastResult | null = neuAbrufen && !frisch
    ? null
    : frisch ?? (gespeichert?.length ? { ok: true, errors: [], belege: gespeichert } : null);

  // Sobald der Entwurf nachtraeglich Belege liefert, die Box aufklappen.
  useEffect(() => {
    if (gespeichert?.length) setOpen(true);
  }, [gespeichert]);

  useEffect(() => {
    if (!open) return;
    elsterApi.listCertificates(companyId)
      .then((d) => {
        setCerts(d.certificates);
        if (d.certificates.length === 1) setCertId(d.certificates[0]!.id);
      })
      .catch(() => setCerts([]));
  }, [open, companyId]);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const res = await taxApi.fetchVast(companyId, year, certId, pin, idnr || undefined);
      setFrisch(res);
      onAbgerufen(res.belege); // im Entwurf sichern, kein zweiter Abruf noetig
      setPin(''); // PIN nicht im State halten
    } catch (e) {
      // Fehlt die IdNr, fragt das Backend gezielt danach.
      if (e && typeof e === 'object' && 'code' in e && (e as { code?: string }).code === 'IDNR_REQUIRED') {
        setNeedIdnr(true);
      }
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900/40 dark:bg-blue-950/20">
        <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
          Daten vom Finanzamt abrufen
        </p>
        <p className="mt-1 text-xs text-blue-800/80 dark:text-blue-300/80">
          Lohnsteuerbescheinigung, Renten und Versicherungsbeiträge liegen beim Finanzamt bereits vor.
          Du kannst sie abrufen und mit deinen Angaben abgleichen.
        </p>
        <div className="mt-3">
          <Button variant="secondary" onClick={() => setOpen(true)}>Belege abrufen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900/40 dark:bg-blue-950/20">
      <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Daten vom Finanzamt abrufen</p>
      {!result && (
        <>
          <p className="mt-1 text-xs text-blue-800/80 dark:text-blue-300/80">
            Der Abruf erfolgt mit deinem ELSTER-Zertifikat – es werden ausschließlich deine eigenen
            Daten geladen. Die ausgewerteten Werte werden mit deinem Entwurf gespeichert, damit du
            sie nicht erneut abrufen musst.
          </p>
          {certs.length === 0 ? (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">
              Kein ELSTER-Zertifikat hinterlegt. Bitte zuerst unter Einstellungen → ELSTER hochladen.
            </p>
          ) : (
            <div className="mt-3 space-y-2">
              <select
                value={certId}
                onChange={(e) => setCertId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
              >
                <option value="">— Zertifikat wählen —</option>
                {certs.map((c) => (
                  <option key={c.id} value={c.id}>{c.label || c.subject || c.id}</option>
                ))}
              </select>
              {needIdnr && (
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-blue-900 dark:text-blue-200">
                    Steuer-Identifikationsnummer (11-stellig)
                  </span>
                  <input
                    value={idnr}
                    onChange={(e) => setIdnr(e.target.value)}
                    placeholder="z. B. 12 345 678 901"
                    inputMode="numeric"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                  />
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                    Steht auf deinem Steuerbescheid – nicht zu verwechseln mit der Steuernummer.
                  </span>
                </label>
              )}
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Zertifikats-PIN"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
              />
              {error ? <ErrorNote error={error} /> : null}
              <div className="flex gap-2">
                <Button onClick={run} busy={busy} disabled={!certId || !pin}>Abrufen</Button>
                <button onClick={() => setOpen(false)} className="text-xs text-gray-500 hover:underline">
                  abbrechen
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {result && (
        <div className="mt-3">
          {!frisch && abrufAm && (
            <p className="mb-2 flex items-center justify-between gap-3 text-xs text-blue-800/80 dark:text-blue-300/80">
              <span>Abgerufen am {new Date(abrufAm).toLocaleDateString('de-DE')}</span>
              <button onClick={() => setNeuAbrufen(true)} className="shrink-0 hover:underline">
                erneut abrufen
              </button>
            </p>
          )}
          {result.belege.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Für {year} liegen beim Finanzamt noch keine Belege vor. Arbeitgeber und Versicherungen
              melden meist bis Ende Februar des Folgejahres.
            </p>
          ) : (
            result.belege.map((b, i) => (
              <div key={`${b.belegart}-${i}`} className="mb-3 last:mb-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{b.bezeichnung}</p>
                {b.werte.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Keine auswertbaren Werte.</p>
                ) : (
                  b.werte.map((w, wi) => (
                    <div key={wi} className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{w.label}</span>
                      <span className="shrink-0 tabular-nums text-gray-900 dark:text-gray-100">
                        {w.cents != null ? euro(w.cents) : (w.anzeige ?? w.text)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            ))
          )}
          {result.errors.length > 0 && (
            <ul className="mt-2 list-inside list-disc text-xs text-amber-700 dark:text-amber-300">
              {result.errors.map((e, i) => <li key={i}>{e.message}</li>)}
            </ul>
          )}
          {(() => {
            // Nur Werte mit Wizard-Feldschluessel lassen sich uebernehmen.
            const uebernehmbar = result.belege
              .flatMap((b) => b.werte)
              .filter((w): w is typeof w & { key: string } =>
                Boolean(w.key) && (typeof w.cents === 'number' || Boolean(w.text)));
            if (uebernehmbar.length === 0) return null;
            return (
              <div className="mt-3">
                <Button
                  // Die Rueckgabe ist die Zahl der WIRKLICH gefuellten Felder.
                  // Vorher wurde gemeldet, wie viele Werte der Beleg hergibt —
                  // auch wenn keiner davon in einem Feld landete.
                  onClick={() => setUebernommen(onUebernehmen(uebernehmbar))}
                  disabled={uebernommen !== null}
                >
                  {uebernommen !== null
                    ? `${uebernommen} Felder ausgefüllt ✓`
                    : `${uebernehmbar.length} Werte in die Erklärung übernehmen`}
                </Button>
              </div>
            );
          })()}
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Übernommene Felder sind mit <span className="rounded bg-blue-100 px-1 font-semibold text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">B</span> markiert (aus Bescheinigung).
            Weitere Werte betreffen Angaben außerhalb deiner Selbständigkeit – trage sie in die
            passenden Felder ein bzw. gleiche sie mit deinen Unterlagen ab.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------- Außergewöhnliche Belastungen (§33 EStG) ----------

const KATEGORIE_LABELS: Record<string, string> = {
  arzt_zahnarzt: 'Arzt / Zahnarzt',
  medikament_rezept: 'Medikamente',
  hilfsmittel: 'Hilfsmittel',
  krankenhaus_kur: 'Klinik / Kur',
  fahrtkosten: 'Fahrtkosten',
  nicht_beguenstigt: 'nicht absetzbar',
};

/**
 * Kapitalertraege-Step mit Krypto-Auswertung.
 *
 * Fachlich der wichtigste Punkt: Krypto gehoert NICHT pauschal in die Anlage
 * KAP. Coins im Direktbesitz sind private Veraeusserungsgeschaefte (§ 23 EStG,
 * Anlage SO), Staking sind sonstige Einkuenfte (§ 22 Nr. 3 EStG, ebenfalls SO).
 * Nur Termingeschaefte (Futures/CFDs/Hebelprodukte) und Wertpapiere laufen
 * ueber § 20 EStG und damit in die KAP. Der Step weist das getrennt aus, damit
 * der Nutzer sieht, welche Zahl wohin gehoert.
 */
function KapitalStep({
  companyId,
  answers,
  setAnswer,
  fields,
  jahr,
}: Hnk35aStepProps & { jahr: number }): ReactElement {
  const fileRef = useRef<HTMLInputElement>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [analyzeError, setAnalyzeError] = useState<unknown>(null);
  const [result, setResult] = useState<KryptoResult | null>(null);
  /**
   * Auftragsnummer der letzten Auswertung. Wird beim naechsten Upload
   * mitgegeben, damit nachgereichte Journale auf den bereits bekannten
   * Transaktionen aufsetzen statt sie zu ersetzen — sonst fehlten die
   * Anschaffungen fuer die Haltefrist des § 23 EStG.
   */
  const [jobId, setJobId] = useState<string | null>(null);
  const [dateien, setDateien] = useState<string[]>([]);
  const [warnungen, setWarnungen] = useState<string[]>([]);
  const [uebernommen, setUebernommen] = useState(false);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;
    setAnalyzeError(null);
    setAnalyzing(true);
    setUebernommen(false);
    setStatusText('Dateien werden übertragen …');
    try {
      const files = await Promise.all(
        picked.map(
          (file) =>
            new Promise<{ data: string; mime: string; name: string }>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const r = reader.result as string;
                const idx = r.indexOf(',');
                resolve({ data: idx >= 0 ? r.slice(idx + 1) : r, mime: file.type, name: file.name });
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            }),
        ),
      );

      // Auswertung laeuft auf dem Server — ein Jahresjournal dauert laenger als
      // das Zeitlimit des Proxys. Hier wird nur der Stand abgefragt; die Seite
      // darf zwischendurch geschlossen werden.
      const { jobId: neu } = await taxApi.analyzeKrypto(companyId, files, jahr, jobId ?? undefined);
      setJobId(neu);

      for (let i = 0; ; i++) {
        const r = await taxApi.kryptoStatus(companyId, neu);
        if (r.status === 'done') {
          setResult(r.ergebnis);
          setDateien(r.dateien);
          setWarnungen(r.warnings);
          break;
        }
        if (r.status === 'failed') throw new Error(r.error ?? 'Die Auswertung ist fehlgeschlagen.');
        setStatusText(
          r.total > 1
            ? `Journale werden gelesen – Datei ${Math.min(r.done + 1, r.total)} von ${r.total} …`
            : 'Transaktionen werden gelesen und nach FIFO abgerechnet …',
        );
        // Grosszuegige Obergrenze: 10 Minuten decken auch eine lange Historie
        // ab, ohne bei einem echten Haenger endlos zu warten.
        if (i >= 300) throw new Error('Die Auswertung dauert ungewöhnlich lange. Bitte später erneut versuchen.');
        await new Promise((res) => setTimeout(res, 2000));
      }
    } catch (err) {
      setAnalyzeError(err);
    } finally {
      setAnalyzing(false);
      setStatusText(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  /**
   * Ergebnis in die Anlagen uebernehmen. Krypto-Veraeusserungen und Staking
   * gehen in die Anlage SO (eigene ELSTER-Container fuer Kryptowerte),
   * Termingeschaefte und Wertpapiere in die Anlage KAP.
   */
  function applyAll() {
    if (!result) return;

    // --- Anlage SO: private Veraeusserungsgeschaefte (§ 23 EStG) ---
    // Nur Geschaefte INNERHALB der Jahresfrist erklaeren; laenger gehaltene
    // Bestaende sind nicht steuerbar und gehoeren nicht in die Aufstellung.
    const steuerbar = result.veraeusserungen.filter((v) => !v.steuerfrei);
    if (steuerbar.length > 0) {
      const erloes = steuerbar.reduce((s, v) => s + v.erloesCents, 0);
      const anschaffung = steuerbar.reduce((s, v) => s + v.anschaffungCents, 0);
      setAnswer('kryptoVeraeusserung', 'true');
      setAnswer('kryptoVeraeusserungspreis', centsToEurStr(erloes));
      setAnswer('kryptoAnschaffungskosten', centsToEurStr(anschaffung));
      setAnswer('kryptoGewinn', centsToEurStr(result.saldoCents));
    }

    // --- Anlage SO: Staking/Lending (§ 22 Nr. 3 EStG) ---
    if (result.stakingCents > 0) {
      setAnswer('kryptoStakingJa', 'true');
      setAnswer('kryptoStakingBetrag', centsToEurStr(result.stakingCents));
    }

    // --- Anlage KAP: § 20 EStG (Wertpapiere + Termingeschaefte) ---
    const kap = result.kapitalertraegeCents + result.terminSaldoCents;
    if (kap > 0) {
      const bisher = eurStrToCents(answers['kapitalOhneAbzug'] ?? '');
      setAnswer('kapitalOhneAbzug', centsToEurStr(bisher + kap));
    }
    setUebernommen(true);
  }

  const kapSumme = result ? result.kapitalertraegeCents + result.terminSaldoCents : 0;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900/40 dark:bg-blue-950/20">
        <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Krypto & Depot auswerten lassen</p>
        <p className="mt-1 text-xs text-blue-800/80 dark:text-blue-300/80">
          Lade deine Börsen-Journale hoch (Bitpanda, Coinbase, Binance, Kraken …) – CSV oder PDF.
          Gewinne, Haltefristen und Staking werden automatisch berechnet und den richtigen Anlagen
          zugeordnet. <strong>Lade die vollständige Historie hoch</strong>, auch aus Vorjahren: für die
          Ein-Jahres-Frist müssen die ursprünglichen Käufe bekannt sein.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv,application/pdf,image/*"
          multiple
          onChange={onFile}
          disabled={analyzing}
          className="mt-3 block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700 dark:text-gray-300"
        />
        {/* Statuszeile: sagt waehrend der Auswertung, was gerade passiert.
            Ohne sie wirkt die Seite bei einer langen Historie eingefroren. */}
        {analyzing && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-blue-100/60 px-3 py-2 dark:bg-blue-900/30">
            <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <span className="text-xs text-blue-900 dark:text-blue-200">
              {statusText ?? 'Auswertung läuft …'}
            </span>
          </div>
        )}
        {/* Bereits gelesene Journale benennen: so ist erkennbar, worauf ein
            weiterer Upload aufsetzt — statt den alten Stand zu ersetzen. */}
        {!analyzing && dateien.length > 0 && (
          <p className="mt-2 text-xs text-blue-800/80 dark:text-blue-300/80">
            Bereits ausgewertet: {dateien.join(', ')}. Weitere Journale kannst du ergänzen – sie
            werden zusammen mit diesen abgerechnet.
          </p>
        )}
        {warnungen.length > 0 && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            Nicht gelesen werden konnte: {warnungen.join(', ')}
          </p>
        )}
        {analyzeError ? <div className="mt-2"><ErrorNote error={analyzeError} /></div> : null}
      </div>

      {result && (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              {result.transaktionen} Transaktionen ausgewertet
              {result.quelle ? ` · ${result.quelle}` : ''}
            </span>
            {/* Auch die Auftragsnummer verwerfen: sonst saehe der naechste
                Upload die alten Transaktionen weiterhin als Vorgeschichte. */}
            <button
              onClick={() => {
                setResult(null); setUebernommen(false);
                setJobId(null); setDateien([]); setWarnungen([]);
              }}
              className="hover:underline"
            >
              zurücksetzen
            </button>
          </div>

          {/* Anlage SO — dort gehoeren Krypto-Verkaeufe und Staking hin. */}
          <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Anlage SO · Private Veräußerungsgeschäfte (§ 23 EStG)
            </p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Gewinne (innerhalb 1 Jahr)</span><span className="tabular-nums">{euro(result.gewinnCents)}</span>
              </div>
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Verluste</span><span className="tabular-nums">− {euro(result.verlustCents)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-1 font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
                <span>Saldo</span><span className="tabular-nums">{euro(result.saldoCents)}</span>
              </div>
              {result.steuerfreiCents !== 0 && (
                <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                  <span>davon steuerfrei (über 1 Jahr gehalten)</span>
                  <span className="tabular-nums">{euro(result.steuerfreiCents)}</span>
                </div>
              )}
              {result.unterFreigrenze && (
                <p className="mt-2 rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
                  Der Saldo liegt unter der Freigrenze von {euro(jahr >= 2024 ? 100000 : 60000)} und bleibt damit
                  steuerfrei (§ 23 Abs. 3 Satz 5 EStG). Achtung: Es ist eine Freigrenze, kein Freibetrag –
                  ein Euro darüber macht den gesamten Gewinn steuerpflichtig.
                </p>
              )}
              {result.stakingCents > 0 && (
                <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>Staking / Lending (§ 22 Nr. 3 EStG)</span>
                    <span className="tabular-nums">{euro(result.stakingCents)}</span>
                  </div>
                  {result.stakingUnterFreigrenze && (
                    <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">
                      Unter der Freigrenze von 256 € – steuerfrei.
                    </p>
                  )}
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Diese Beträge gehören in die <strong>Anlage SO</strong> (nicht in die KAP) – ELSTER hat dort
              eigene Felder für Kryptowerte. Sie werden mit übernommen.
            </p>
          </div>

          {/* Anlage KAP — nur § 20: Wertpapiere und Termingeschaefte. */}
          <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Anlage KAP · Kapitalerträge (§ 20 EStG)
            </p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Wertpapiere (Aktien, ETFs)</span>
                <span className="tabular-nums">{euro(result.kapitalertraegeCents)}</span>
              </div>
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Termingeschäfte (Futures, CFDs, Hebelprodukte)</span>
                <span className="tabular-nums">{euro(result.terminSaldoCents)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-1 font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
                <span>Summe für die Anlage KAP</span><span className="tabular-nums">{euro(kapSumme)}</span>
              </div>
              {result.terminVerlustCents > 0 && (
                <p className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                  Verluste aus Termingeschäften sind nur mit Gewinnen aus Termingeschäften verrechenbar
                  (§ 20 Abs. 6 Satz 5 EStG) und gehören in eine eigene Zeile der Anlage KAP.
                </p>
              )}
            </div>
          </div>

          {/* Uebernahme in beide Anlagen — eine Aktion, damit nichts vergessen wird. */}
          <button
            type="button"
            onClick={applyAll}
            disabled={uebernommen}
            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
          >
            {uebernommen
              ? '✓ In Anlage SO und KAP übernommen'
              : 'Werte in die Steuererklärung übernehmen (Anlage SO + KAP)'}
          </button>

          {/* Einzelne Veraeusserungen: nachvollziehbar machen, was gerechnet wurde. */}
          {result.veraeusserungen.length > 0 && (
            <details className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <summary className="cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-100">
                {result.veraeusserungen.length} Veräußerungen im Detail (FIFO)
              </summary>
              <div className="mt-2 max-h-72 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500 dark:text-gray-400">
                    <tr className="text-left">
                      <th className="py-1">Asset</th><th>Kauf</th><th>Verkauf</th>
                      <th className="text-right">Tage</th><th className="text-right">Gewinn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.veraeusserungen.map((v, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-1 font-medium text-gray-900 dark:text-gray-100">{v.asset}</td>
                        <td className="text-gray-600 dark:text-gray-400">{v.kaufDatum}</td>
                        <td className="text-gray-600 dark:text-gray-400">{v.verkaufDatum}</td>
                        <td className="text-right text-gray-600 dark:text-gray-400">{v.haltedauerTage}</td>
                        <td className={`text-right tabular-nums ${v.steuerfrei ? 'text-emerald-600 dark:text-emerald-400' : v.gewinnCents < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-gray-100'}`}>
                          {euro(v.gewinnCents)}{v.steuerfrei ? ' *' : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-xs text-gray-400">* über ein Jahr gehalten – steuerfrei</p>
              </div>
            </details>
          )}

          {(result.warnungen.length > 0 || result.hinweise.length > 0) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
              <p className="font-medium">Bitte prüfen</p>
              <ul className="mt-1 list-inside list-disc space-y-0.5">
                {[...result.warnungen, ...result.hinweise].slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Die normalen KAP-Felder bleiben erhalten (Steuerbescheinigung der Bank). */}
      {fields.map((f) => (
        <WizardFieldInput
          key={f.key}
          field={f}
          value={answers[f.key] ?? ''}
          onChange={(v) => setAnswer(f.key, v)}
          answers={answers}
        />
      ))}
    </div>
  );
}

/**
 * Krankheitskosten-Step: Anders als die Betriebskostenabrechnung faellt hier
 * typischerweise EINE VIELZAHL von Belegen ueber das Jahr an. Deshalb sammelt
 * dieser Step die Uploads auf und summiert laufend, statt jeden Beleg einzeln
 * zu ersetzen.
 */
function AgbStep({ companyId, answers, setAnswer, fields }: Hnk35aStepProps): ReactElement {
  const fileRef = useRef<HTMLInputElement>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<unknown>(null);
  // Bei mehreren Belegen zeigt der Fortschritt, der wievielte gerade laeuft —
  // sonst steht minutenlang dieselbe Zeile da.
  const [fortschritt, setFortschritt] = useState<{ i: number; n: number } | null>(null);
  const [belege, setBelege] = useState<{ name: string; result: KrankheitsResult }[]>([]);

  const summeAufwand = belege.reduce((s, b) => s + b.result.summeAufwendungenCents, 0);
  const summeErstattung = belege.reduce((s, b) => s + b.result.summeErstattungenCents, 0);
  const hinweise = [...new Set(belege.flatMap((b) => b.result.hinweise))];

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;
    setAnalyzeError(null);
    setAnalyzing(true);
    try {
      // Anders als bei der Betriebskostenabrechnung ist hier jede Datei ein
      // EIGENER Beleg — also je Datei ein Aufruf, Ergebnisse werden addiert.
      for (const [idx, file] of picked.entries()) {
        setFortschritt({ i: idx + 1, n: picked.length });
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const idx = result.indexOf(',');
            resolve(idx >= 0 ? result.slice(idx + 1) : result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const res = await taxApi.analyzeKrankheitskosten(companyId, base64, file.type);
        setBelege((prev) => [...prev, { name: file.name, result: res }]);
      }
    } catch (err) {
      setAnalyzeError(err);
    } finally {
      setAnalyzing(false);
      setFortschritt(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function applyValues() {
    setAnswer('krankheitskosten', centsToEurStr(summeAufwand));
    setAnswer('krankheitsErstattungen', centsToEurStr(summeErstattung));
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900/40 dark:bg-blue-950/20">
        <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Belege auswerten lassen</p>
        <p className="mt-1 text-xs text-blue-800/80 dark:text-blue-300/80">
          Arzt- oder Zahnarztrechnung, Rezept, Beleg für Brille oder Zuzahlung hochladen – du kannst
          mehrere nacheinander hinzufügen, die Beträge werden zusammengezählt.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/*"
          multiple
          onChange={onFile}
          disabled={analyzing}
          className="mt-3 block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700 dark:text-gray-300"
        />
        {analyzing && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-blue-100/60 px-3 py-2 dark:bg-blue-900/30">
            <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <span className="text-xs text-blue-900 dark:text-blue-200">
              {fortschritt && fortschritt.n > 1
                ? `Beleg ${fortschritt.i} von ${fortschritt.n} wird gelesen …`
                : 'Beleg wird gelesen …'}
            </span>
          </div>
        )}
        {analyzeError ? <div className="mt-2"><ErrorNote error={analyzeError} /></div> : null}
      </div>

      {belege.length > 0 && (
        <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {belege.length} {belege.length === 1 ? 'Beleg' : 'Belege'} ausgewertet
            </span>
            <button
              onClick={() => setBelege([])}
              className="text-xs text-gray-500 hover:underline dark:text-gray-400"
            >
              zurücksetzen
            </button>
          </div>
          {belege.map((b, bi) => (
            <div key={`${b.name}-${bi}`} className="mb-3 last:mb-0">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{b.name}</p>
              {b.result.positionen.map((p, i) => (
                <div key={i} className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
                  <span className={p.kategorie === 'nicht_beguenstigt' ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-300'}>
                    {p.bezeichnung}
                    <span className="ml-2 text-xs text-gray-400">{KATEGORIE_LABELS[p.kategorie] ?? p.kategorie}</span>
                  </span>
                  <span className="shrink-0 tabular-nums text-gray-900 dark:text-gray-100">
                    {euro(p.betragCents)}
                    {p.erstattungCents > 0 && (
                      <span className="ml-2 text-xs text-green-600 dark:text-green-400">− {euro(p.erstattungCents)}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ))}
          <div className="mt-3 border-t border-gray-200 pt-3 text-sm dark:border-gray-700">
            <div className="flex justify-between font-medium text-gray-900 dark:text-gray-100">
              <span>Aufwendungen gesamt</span><span className="tabular-nums">{euro(summeAufwand)}</span>
            </div>
            <div className="flex justify-between text-gray-600 dark:text-gray-400">
              <span>davon erstattet</span><span className="tabular-nums">{euro(summeErstattung)}</span>
            </div>
          </div>
          {hinweise.length > 0 && (
            <ul className="mt-3 list-inside list-disc text-xs text-amber-700 dark:text-amber-300">
              {hinweise.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          )}
          <div className="mt-3">
            <Button onClick={applyValues}>Werte übernehmen</Button>
          </div>
        </div>
      )}

      {fields.map((f) => (
        <WizardFieldInput
          key={f.key}
          field={f}
          value={answers[f.key] ?? ''}
          onChange={(v) => setAnswer(f.key, v)}
          answers={answers}
        />
      ))}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Krankheitskosten wirken sich erst aus, wenn sie deine zumutbare Belastung übersteigen. Wie hoch
        die ist, hängt von Einkommen, Familienstand und Kinderzahl ab – das rechnet das Finanzamt
        automatisch aus. Trag die Kosten also auch dann ein, wenn du unsicher bist.
      </p>
    </div>
  );
}

/**
 * Werbungskosten mit Adressvervollstaendigung und Entfernungsermittlung.
 *
 * Die Adresse der Arbeitsstaette wird wie bei den Nachweisen vorgeschlagen;
 * die einfache Entfernung holt der Server ueber Maps und rundet sie AB
 * (§ 9 Abs. 1 Satz 3 Nr. 4 EStG zaehlt nur volle Kilometer).
 */
function WerbungskostenStep({
  companyId, answers, setAnswer, fields, ausBescheinigung,
}: {
  companyId: string;
  answers: Answers;
  setAnswer: (k: string, v: string) => void;
  fields: WizardField[];
  ausBescheinigung: Set<string>;
}): ReactElement {
  const [vorschlaege, setVorschlaege] = useState<string[]>([]);
  const [sucheOffen, setSucheOffen] = useState(false);
  const [kmBusy, setKmBusy] = useState(false);
  const [kmInfo, setKmInfo] = useState<string | null>(null);
  const [kmError, setKmError] = useState<unknown>(null);

  const adresse = answers['wkArbeitsstaette'] ?? '';

  // Vorschlaege verzoegert abrufen, damit nicht jeder Tastendruck eine
  // Anfrage ausloest.
  useEffect(() => {
    if (!sucheOffen || adresse.trim().length < 3) {
      setVorschlaege([]);
      return;
    }
    const t = setTimeout(() => {
      taxApi.wizardAddressSuggest(companyId, adresse).then(setVorschlaege).catch(() => setVorschlaege([]));
    }, 300);
    return () => clearTimeout(t);
  }, [companyId, adresse, sucheOffen]);

  async function entfernungBerechnen(ziel: string) {
    if (!ziel.trim()) return;
    setKmBusy(true);
    setKmError(null);
    setKmInfo(null);
    try {
      const r = await taxApi.wizardEntfernung(companyId, ziel);
      setAnswer('wkEntfernungKm', String(r.distanceKm));
      setKmInfo(
        `${r.distanceKm} km einfache Entfernung (Fahrtstrecke ${r.exaktKm.toLocaleString('de-DE')} km, auf volle km abgerundet). Ab ${r.vonAufgeloest}.`,
      );
    } catch (e) {
      setKmError(e);
    } finally {
      setKmBusy(false);
    }
  }

  // Das Adressfeld und die Entfernung rendern wir selbst, den Rest generisch.
  const rest = fields.filter((f) => f.key !== 'wkArbeitsstaette' && f.key !== 'wkEntfernungKm');
  const kmFeld = fields.find((f) => f.key === 'wkEntfernungKm');

  return (
    <div className="space-y-4">
      <div className="relative">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Adresse der Arbeitsstätte
          </span>
          <input
            value={adresse}
            onChange={(e) => { setAnswer('wkArbeitsstaette', e.target.value); setSucheOffen(true); }}
            onFocus={() => setSucheOffen(true)}
            // Kurz verzoegert schliessen, sonst greift der Klick auf einen
            // Vorschlag nicht mehr (blur feuert zuerst).
            onBlur={() => setTimeout(() => setSucheOffen(false), 150)}
            placeholder="Straße, PLZ Ort"
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          />
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            Tippen und aus den Vorschlägen wählen – die Entfernung wird dann automatisch berechnet.
          </span>
        </label>
        {sucheOffen && vorschlaege.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
            {vorschlaege.map((v) => (
              <li key={v}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setAnswer('wkArbeitsstaette', v);
                    setSucheOffen(false);
                    void entfernungBerechnen(v);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {v}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {kmFeld && (
        <div>
          <WizardFieldInput
            field={kmFeld}
            value={answers['wkEntfernungKm'] ?? ''}
            onChange={(v) => setAnswer('wkEntfernungKm', v)}
            answers={answers}
            ausBescheinigung={ausBescheinigung.has('wkEntfernungKm')}
          />
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => void entfernungBerechnen(adresse)}
              busy={kmBusy}
              disabled={!adresse.trim()}
            >
              Entfernung berechnen
            </Button>
            {kmInfo && <span className="text-xs text-gray-500 dark:text-gray-400">{kmInfo}</span>}
          </div>
          {kmError ? <div className="mt-2"><ErrorNote error={kmError} /></div> : null}
        </div>
      )}

      {rest.map((f) => (
        <WizardFieldInput
          key={f.key}
          field={f}
          value={answers[f.key] ?? ''}
          onChange={(v) => setAnswer(f.key, v)}
          answers={answers}
          ausBescheinigung={ausBescheinigung.has(f.key)}
        />
      ))}
    </div>
  );
}

// ---------- §35a / HNK step ----------

interface Hnk35aStepProps {
  companyId: string;
  answers: Answers;
  setAnswer: (key: string, value: string) => void;
  fields: WizardField[];
}

function Hnk35aStep({ companyId, answers, setAnswer, fields }: Hnk35aStepProps): ReactElement {
  const fileRef = useRef<HTMLInputElement>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<unknown>(null);
  const [positionen, setPositionen] = useState<Hnk35aPosition[] | null>(null);
  const [sumHhn, setSumHhn] = useState<number | null>(null);
  const [sumHwk, setSumHwk] = useState<number | null>(null);
  const [hinweise, setHinweise] = useState<string[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  // Sagt waehrend der Auswertung, was gerade passiert. Die KI-Analyse dauert
  // je nach Seitenzahl einige Sekunden — ohne Rueckmeldung wirkt die Seite
  // eingefroren und der Kunde klickt ein zweites Mal.
  const [statusText, setStatusText] = useState<string | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;
    setAnalyzeError(null);
    setAnalyzing(true);
    setStatusText(picked.length > 1 ? `${picked.length} Seiten werden gelesen …` : 'Datei wird gelesen …');
    try {
      // Alle Seiten gemeinsam auswerten — eine Abrechnung kann sich ueber
      // mehrere Seiten/Scans erstrecken.
      const files = await Promise.all(picked.map(async (file) => ({
        mime: file.type,
        data: await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const idx = result.indexOf(',');
            resolve(idx >= 0 ? result.slice(idx + 1) : result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        }),
      })));
      setFileNames(picked.map((f) => f.name));
      setStatusText(
        picked.length > 1
          ? `${picked.length} Seiten werden gemeinsam ausgewertet — das dauert einen Moment …`
          : 'Abrechnung wird ausgewertet — das dauert einen Moment …',
      );
      const res = await taxApi.analyzeBetriebskosten(companyId, files);
      setPositionen(res.positionen);
      setSumHhn(res.summeHaushaltsnahCents);
      setSumHwk(res.summeHandwerkerCents);
      setHinweise(res.hinweise);
    } catch (err) {
      setAnalyzeError(err);
    } finally {
      setAnalyzing(false);
      setStatusText(null);
      // Reset file input so same file can be re-uploaded.
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function applyValues() {
    // Summen UND Einzelaufstellung setzen — ELSTER verlangt zur Summe
    // zwingend eine Aufstellung (Art + Betrag), sonst wird sie beanstandet.
    const bezeichnungen = (kat: string): string =>
      (positionen ?? [])
        .filter((p) => p.kategorie === kat && p.arbeitsanteilCents > 0)
        .map((p) => p.bezeichnung)
        .join(', ')
        .slice(0, 200);

    if (sumHhn !== null) {
      setAnswer('haushaltsnahArbeit', centsToEurStr(sumHhn));
      setAnswer('haushaltsnahEinzel', centsToEurStr(sumHhn));
      setAnswer('haushaltsnahArt', bezeichnungen('haushaltsnahe_dienstleistung') || 'Haushaltsnahe Dienstleistungen');
    }
    if (sumHwk !== null) {
      setAnswer('handwerkerArbeit', centsToEurStr(sumHwk));
      setAnswer('handwerkerEinzel', centsToEurStr(sumHwk));
      setAnswer('handwerkerArt', bezeichnungen('handwerkerleistung') || 'Handwerkerleistungen');
      // Rechnungsbetrag: soweit erkannt der Gesamtbetrag, sonst der Lohnanteil.
      const gesamt = (positionen ?? [])
        .filter((p) => p.kategorie === 'handwerkerleistung')
        .reduce((s, p) => s + (p.gesamtCents ?? p.arbeitsanteilCents), 0);
      setAnswer('handwerkerRechnung', centsToEurStr(gesamt || sumHwk));
    }
  }

  return (
    <div className="space-y-4">
      {/* Normal fields for this step */}
      {fields
        .filter((f) => f.key !== 'haushaltsnahArbeit' && f.key !== 'handwerkerArbeit')
        .map((f) => (
          <WizardFieldInput
            key={f.key}
            field={f}
            value={answers[f.key] ?? ''}
            onChange={(v) => setAnswer(f.key, v)}
            answers={answers}
          />
        ))}

      {/* Haushaltsnahe Arbeit */}
      {fields.find((f) => f.key === 'haushaltsnahArbeit') && (
        <WizardFieldInput
          key="haushaltsnahArbeit"
          field={fields.find((f) => f.key === 'haushaltsnahArbeit')!}
          value={answers['haushaltsnahArbeit'] ?? ''}
          onChange={(v) => setAnswer('haushaltsnahArbeit', v)}
          answers={answers}
        />
      )}
      {/* Handwerker */}
      {fields.find((f) => f.key === 'handwerkerArbeit') && (
        <WizardFieldInput
          key="handwerkerArbeit"
          field={fields.find((f) => f.key === 'handwerkerArbeit')!}
          value={answers['handwerkerArbeit'] ?? ''}
          onChange={(v) => setAnswer('handwerkerArbeit', v)}
          answers={answers}
        />
      )}

      {/* Betriebskostenabrechnung-Analyse */}
      <div className="rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-600">
        <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Betriebskostenabrechnung analysieren (§35a)
        </p>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Lade deine Betriebskostenabrechnung hoch (PDF oder Bild). Saldify erkennt automatisch
          haushaltsnahe Dienstleistungen und Handwerkerleistungen. Bei mehreren Seiten kannst du
          alle Dateien zusammen auswählen – sie werden als eine Abrechnung ausgewertet.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          onChange={onFile}
          className="hidden"
        />
        <Button
          variant="secondary"
          onClick={() => fileRef.current?.click()}
          busy={analyzing}
          disabled={analyzing}
        >
          Abrechnung hochladen &amp; analysieren
        </Button>
        {/* Statuszeile mit Spinner — gleiche Darstellung wie beim
            Krypto-Schritt, damit der Wizard sich einheitlich anfuehlt. */}
        {analyzing && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-blue-100/60 px-3 py-2 dark:bg-blue-900/30">
            <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <span className="text-xs text-blue-900 dark:text-blue-200">
              {statusText ?? 'Auswertung läuft …'}
            </span>
          </div>
        )}
        {!analyzing && fileNames.length > 0 && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Ausgewertet: {fileNames.join(', ')}
          </p>
        )}
        {analyzeError ? <div className="mt-2"><ErrorNote error={analyzeError} /></div> : null}
      </div>

      {positionen && positionen.length > 0 && (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-2 pr-4 font-medium text-gray-700 dark:text-gray-300">Bezeichnung</th>
                  <th className="pb-2 pr-4 font-medium text-gray-700 dark:text-gray-300">Kategorie</th>
                  <th className="pb-2 pr-4 text-right font-medium text-gray-700 dark:text-gray-300">Arbeitsanteil</th>
                  <th className="pb-2 font-medium text-gray-700 dark:text-gray-300">Konfidenz</th>
                </tr>
              </thead>
              <tbody>
                {positionen.map((p, i) => {
                  const lowConf = p.confidence < 0.6;
                  return (
                    <tr
                      key={i}
                      className={`border-b border-gray-100 last:border-0 dark:border-gray-700 ${lowConf ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}
                    >
                      <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{p.bezeichnung}</td>
                      <td className="py-2 pr-4 text-gray-600 dark:text-gray-300">{p.kategorie}</td>
                      <td className="py-2 pr-4 text-right text-gray-900 dark:text-gray-100">
                        {euro(p.arbeitsanteilCents)}
                      </td>
                      <td className="py-2">
                        {lowConf ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            bitte prüfen
                          </span>
                        ) : (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/40 dark:text-green-300">
                            {Math.round(p.confidence * 100)} %
                          </span>
                        )}
                        {lowConf && p.begruendung && (
                          <span className="mt-0.5 block text-xs text-amber-600 dark:text-amber-400">
                            {p.begruendung}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hinweise.length > 0 && (
            <ul className="space-y-1">
              {hinweise.map((h, i) => (
                <li key={i} className="text-xs text-gray-500 dark:text-gray-400">
                  {h}
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-4 rounded-xl bg-gray-50 px-4 py-3 text-sm dark:bg-gray-900">
            <span className="text-gray-600 dark:text-gray-300">
              Haushaltsnahe Dienste: <strong>{sumHhn !== null ? euro(sumHhn) : '–'}</strong>
            </span>
            <span className="text-gray-600 dark:text-gray-300">
              Handwerkerleistungen: <strong>{sumHwk !== null ? euro(sumHwk) : '–'}</strong>
            </span>
            <Button onClick={applyValues}>Werte übernehmen</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- progress indicator ----------

/**
 * Fortschrittsleiste mit Anlagen-Kurznamen. Die Punkte sind anklickbar —
 * bei 15 Schritten ist Durchklicken sonst der einzige Weg zurueck.
 */
function StepProgress({
  steps, current, onJump,
}: {
  steps: WizardStep[];
  current: number;
  onJump: (i: number) => void;
}): ReactElement {
  return (
    <div className="mb-6 flex items-start gap-1 overflow-x-auto pb-1">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-start gap-1">
          <button
            onClick={() => onJump(i)}
            title={s.title}
            className="flex w-16 shrink-0 flex-col items-center gap-1 rounded-lg px-0.5 py-1 transition hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                i < current
                  ? 'bg-blue-600 text-white'
                  : i === current
                  ? 'bg-blue-600 text-white ring-2 ring-blue-200'
                  : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              {i < current ? '✓' : i + 1}
            </span>
            <span
              className={`w-full truncate text-center text-[10px] leading-tight ${
                i === current
                  ? 'font-semibold text-blue-700 dark:text-blue-300'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {s.kurz ?? s.title}
            </span>
          </button>
          {i < steps.length - 1 && (
            <div
              className={`mt-3 h-0.5 w-3 shrink-0 ${i < current ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- summary screen ----------

/**
 * Steuerprognose aus den erfassten Angaben — wie ELSTER sie nach dem
 * Ausfuellen zeigt. Rechnet im Backend, damit Tarif und Grenzen an einer
 * Stelle liegen.
 */
function PrognoseBox({
  companyId, year, answers, catalog,
}: {
  companyId: string;
  year: number;
  answers: Answers;
  catalog: WizardCatalog;
}): ReactElement | null {
  const [p, setP] = useState<EstPrognose | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    // Antworten ins Backend-Format bringen (Geld in Cent) — gleiche Regeln
    // wie beim Speichern, damit die Prognose zum XML passt.
    const payload: Record<string, unknown> = {};
    for (const step of catalog.steps) {
      for (const f of step.fields) {
        const keys = step.repeatable
          ? Array.from({ length: step.repeatable.max }, (_, n) => `${step.key}.${n}.${f.key}`)
          : [f.key];
        for (const k of keys) {
          const raw = answers[k];
          if (raw === undefined || raw === '') continue;
          if (f.type === 'money' || f.type === 'moneyEur') payload[k] = eurStrToCents(raw);
          else if (f.type === 'bool' || f.type === 'check') payload[k] = raw === 'true';
          else if (f.type === 'number') payload[k] = Number(raw);
          else payload[k] = raw;
        }
      }
    }
    setBusy(true);
    taxApi.estPrognose(companyId, year, payload)
      .then(setP)
      .catch(() => setP(null))
      .finally(() => setBusy(false));
  }, [companyId, year, answers, catalog]);

  if (busy) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Prognose wird berechnet …</p>;
  }
  if (!p) return null;

  const erstattung = p.erstattungCents;
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Voraussichtliches Ergebnis {p.year}
        </h3>
        {p.splitting && <span className="text-xs text-gray-500">Splittingtarif</span>}
      </div>

      <div className="space-y-0.5 text-sm">
        {p.posten.map((x, i) => (
          <div key={i} className="flex items-baseline justify-between gap-3">
            <span className="text-gray-600 dark:text-gray-400">
              {x.abzug ? '− ' : ''}{x.label}
            </span>
            <span className="shrink-0 tabular-nums text-gray-800 dark:text-gray-200">{euro(x.cents)}</span>
          </div>
        ))}
        <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-gray-200 pt-2 font-medium dark:border-gray-700">
          <span className="text-gray-700 dark:text-gray-300">Zu versteuerndes Einkommen</span>
          <span className="shrink-0 tabular-nums text-gray-900 dark:text-gray-100">{euro(p.zveCents)}</span>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-gray-600 dark:text-gray-400">Einkommensteuer</span>
          <span className="shrink-0 tabular-nums text-gray-800 dark:text-gray-200">{euro(p.tariflicheSteuerCents)}</span>
        </div>
        {p.ermaessigung35aCents > 0 && (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-gray-600 dark:text-gray-400">− Steuerermäßigung §35a</span>
            <span className="shrink-0 tabular-nums text-green-700 dark:text-green-400">{euro(p.ermaessigung35aCents)}</span>
          </div>
        )}
        {p.anrechnungCents > 0 && (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-gray-600 dark:text-gray-400">− Bereits gezahlte Lohnsteuer</span>
            <span className="shrink-0 tabular-nums text-green-700 dark:text-green-400">{euro(p.anrechnungCents)}</span>
          </div>
        )}
      </div>

      <div
        className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold ${
          erstattung >= 0
            ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
            : 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
        }`}
      >
        {erstattung >= 0
          ? `Voraussichtliche Erstattung: ${euro(erstattung)}`
          : `Voraussichtliche Nachzahlung: ${euro(-erstattung)}`}
      </div>

      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        Schätzung auf Basis deiner Angaben – ohne Kinderfreibeträge, Progressionsvorbehalt und
        Günstigerprüfungen. Verbindlich ist der Bescheid des Finanzamts.
      </p>
      {p.hinweise.length > 0 && (
        <ul className="mt-2 list-inside list-disc text-xs text-gray-500 dark:text-gray-400">
          {p.hinweise.map((h, i) => <li key={i}>{h}</li>)}
        </ul>
      )}
    </div>
  );
}

function SummaryScreen({
  companyId,
  catalog,
  answers,
  onBack,
  onSave,
  saving,
}: {
  companyId: string;
  catalog: WizardCatalog;
  answers: Answers;
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
}): ReactElement {
  return (
    <div className="space-y-4">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">Zusammenfassung</h2>
      {/* Fehlende Pflichtangaben vorab nennen — sonst erfaehrt man sie erst
          aus der ELSTER-Fehlerliste nach dem Speichern. */}
      {(() => {
        const fehlend = catalog.steps
          .filter((s) => !s.optionalKey || answers[`auswahl.${s.optionalKey}`] === 'true')
          .flatMap((s) =>
            s.repeatable ? [] : s.fields.filter((f) => f.required && !(answers[f.key] ?? '')).map((f) => f.label),
          );
        if (fehlend.length === 0) return null;
        return (
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            <p className="font-medium">Diese Pflichtangaben fehlen noch:</p>
            <ul className="mt-1 list-inside list-disc text-xs">
              {fehlend.map((l) => <li key={l}>{l}</li>)}
            </ul>
            <p className="mt-1 text-xs">ELSTER weist die Erklärung sonst zurück.</p>
          </div>
        );
      })()}
      <PrognoseBox companyId={companyId} year={catalog.year} answers={answers} catalog={catalog} />
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Bitte überprüfe deine Angaben, bevor du die Erklärung speicherst.
      </p>
      {catalog.steps.filter((step) => !step.optionalKey || answers[`auswahl.${step.optionalKey}`] === 'true').map((step) => {
        const filledFields = step.fields.filter(
          (f) => answers[f.key] !== undefined && answers[f.key] !== '',
        );
        if (filledFields.length === 0) return null;
        return (
          <div key={step.key} className="rounded-xl border border-gray-100 dark:border-gray-700">
            <div className="border-b border-gray-100 px-4 py-2 dark:border-gray-700">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {step.title}
              </span>
            </div>
            <div className="divide-y divide-gray-50 dark:divide-gray-700">
              {filledFields.map((f) => (
                <div key={f.key} className="flex justify-between px-4 py-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-300">{f.label}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {anzeigeWert(f, answers[f.key] ?? '')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onBack}>
          Zurück
        </Button>
        <Button onClick={onSave} busy={saving}>
          Speichern &amp; zur Übermittlung
        </Button>
      </div>
    </div>
  );
}

// ---------- main wizard ----------

export function EstWizard({ companyId, year, onClose }: Props): ReactElement {
  const [catalog, setCatalog] = useState<WizardCatalog | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [stepIdx, setStepIdx] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [savedPeriod, setSavedPeriod] = useState<string | null>(null);
  /** Ein gespeicherter Entwurf wurde geladen (Hinweis fuer den Nutzer). */
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  /** Felder, die aus einer Bescheinigung uebernommen wurden (Badge "B"). */
  const [ausBescheinigung, setAusBescheinigung] = useState<Set<string>>(new Set());
  /**
   * Zuletzt abgerufene Bescheinigungen — im Entwurf gespeichert, damit der
   * Abruf (Zertifikat + PIN) nicht bei jedem Oeffnen wiederholt werden muss.
   */
  const [vastBelege, setVastBelege] = useState<VastResult['belege'] | null>(null);
  const [vastAbrufAm, setVastAbrufAm] = useState<string | null>(null);

  /**
   * Bescheinigungs-Daten fuer den Entwurf. Bewusst NEBEN "answers", damit der
   * Datenverlust-Schutz (der nur answers zaehlt) davon unberuehrt bleibt.
   */
  function vastMeta(): Record<string, unknown> {
    const meta: Record<string, unknown> = {};
    if (ausBescheinigung.size) meta['ausBescheinigung'] = [...ausBescheinigung];
    if (vastBelege) meta['vast'] = { abrufAm: vastAbrufAm, belege: vastBelege };
    return meta;
  }

  /**
   * Uebernimmt die abgerufenen VaSt-Werte in die Wizard-Felder und merkt sich,
   * welche Felder aus einer Bescheinigung stammen.
   *
   * Prueft jeden Schluessel gegen den Katalog. Frueher wurde blind geschrieben:
   * zeigte ein Schluessel ins Leere, galt der Wert als uebernommen, landete
   * aber in keinem Feld. Unbekannte Schluessel werden jetzt gemeldet statt
   * verschluckt.
   */
  function uebernehmeVastWerte(werte: { key: string; cents?: number; text?: string }[]): number {
    if (!catalog) return 0;
    const bekannt = new Set(catalog.steps.flatMap((s) => s.fields.map((f) => f.key)));
    const keys = new Set<string>();
    const unbekannt: string[] = [];

    setAnswers((prev) => {
      const next = { ...prev };
      for (const w of werte) {
        // Belege aus einem frueheren Abruf koennen noch alte Schluessel tragen.
        const key = VAST_ALTSCHLUESSEL[w.key] ?? w.key;
        if (!bekannt.has(key)) { unbekannt.push(w.key); continue; }
        next[key] = typeof w.cents === 'number' ? centsToEurStr(w.cents) : (w.text ?? '');
        keys.add(key);
      }
      return next;
    });

    if (unbekannt.length) {
      // Sichtbar machen statt still schlucken — sonst sucht man den fehlenden
      // Wert im Formular, obwohl er nie irgendwo ankam.
      console.warn('VaSt: unbekannte Feldschlüssel übersprungen:', unbekannt);
    }
    setAusBescheinigung((prev) => new Set([...prev, ...keys]));
    setDraftSavedAt(null);
    return keys.size;
  }

  /** Auswahlseite: welche Anlagen sind relevant? Startet den Assistenten. */
  const [showAuswahl, setShowAuswahl] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Katalog UND einen ggf. vorhandenen Entwurf laden. Ohne den Entwurf
    // begaenne man nach jedem Verlassen des Assistenten wieder bei null.
    Promise.all([
      taxApi.estWizard(companyId, year),
      taxApi.listReturns(companyId).then(
        (list) => list.find((e) => e.type === 'est' && e.period === String(year)) ?? null,
        () => null,
      ),
    ])
      .then(([{ catalog: c, prefill }, entry]) => {
        setCatalog(c);
        const saved = ((entry?.data as { answers?: Record<string, unknown> } | undefined)?.answers ?? {}) as Record<string, unknown>;

        const toStr = (field: WizardField, raw: unknown): string =>
          field.type === 'money' || field.type === 'moneyEur' ? centsToEurStr(Number(raw))
            : field.type === 'bool' || field.type === 'check' ? String(raw === true || raw === 'true')
            : String(raw);

        const initial: Answers = {};
        for (const step of c.steps) {
          for (const field of step.fields) {
            const keys = step.repeatable
              ? Array.from({ length: step.repeatable.max }, (_, n) => `${step.key}.${n}.${field.key}`)
              : [field.key];
            for (const key of keys) {
              // Gespeicherter Wert schlaegt die Vorbefuellung — sonst wuerde
              // eine Korrektur des Nutzers beim naechsten Oeffnen ueberschrieben.
              if (saved[key] !== undefined && saved[key] !== null) {
                initial[key] = toStr(field, saved[key]);
                continue;
              }
              if (step.repeatable) continue; // Prefill gibt es nur fuer Einzelfelder
              const raw = prefill[field.prefillFrom ?? field.key] ?? prefill[field.key];
              if (raw !== undefined) initial[key] = toStr(field, raw);
            }
          }
        }
        // Auswahl der Anlagen ebenfalls wiederherstellen (liegt als
        // "auswahl.<key>" im Entwurf und steuert die sichtbaren Schritte).
        for (const a of c.auswahl) {
          const k = `auswahl.${a.key}`;
          if (saved[k] !== undefined) initial[k] = saved[k] === true || saved[k] === 'true' ? 'true' : 'false';
        }
        setAnswers(initial);
        // Nur markieren, dass ein Entwurf existiert — NICHT savedPeriod setzen,
        // das wuerde direkt zur Uebermittlungsansicht springen.
        if (entry && Object.keys(saved).length > 0) setDraftLoaded(true);

        // Bescheinigungen des letzten Abrufs wiederherstellen. Ohne das muesste
        // der Nutzer Zertifikat und PIN bei jedem Oeffnen erneut eingeben.
        const daten = entry?.data as
          | { ausBescheinigung?: unknown; vast?: { abrufAm?: string | null; belege?: VastResult['belege'] } }
          | undefined;
        if (Array.isArray(daten?.ausBescheinigung)) {
          setAusBescheinigung(new Set(daten.ausBescheinigung.filter((k): k is string => typeof k === 'string')));
        }
        if (daten?.vast?.belege) {
          setVastBelege(daten.vast.belege);
          setVastAbrufAm(daten.vast.abrufAm ?? null);
        }
      })
      .catch(setLoadError)
      .finally(() => setLoading(false));
  }, [companyId, year]);

  function clearAnswer(key: string) {
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function setAnswer(key: string, value: string) {
    // Nach einer Aenderung ist der gespeicherte Stand veraltet — sonst zeigt
    // der Knopf weiter "Gespeichert", obwohl es Ungesichertes gibt.
    setDraftSavedAt(null);
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }

  /** Antworten in das Backend-Format bringen (Geld in Cent). */
  function buildPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    if (!catalog) return payload;
    const put = (key: string, field: WizardField): void => {
      const raw = answers[key];
      if (raw === undefined || raw === '') return;
      if (field.type === 'money' || field.type === 'moneyEur') payload[key] = eurStrToCents(raw);
      else if (field.type === 'bool' || field.type === 'check') payload[key] = raw === 'true';
      else if (field.type === 'number') payload[key] = Number(raw);
      else payload[key] = raw;
    };
    // Auswahl nur beruecksichtigen, wenn der Nutzer sie ueberhaupt getroffen
    // hat. Sonst wuerde ein Entwurf ohne gespeicherte Auswahl beim Speichern
    // alle optionalen Anlagen verlieren.
    const auswahlGesetzt = catalog.auswahl.some((a) => answers[`auswahl.${a.key}`] !== undefined);
    for (const step of catalog.steps) {
      // Abgewaehlte Anlagen nicht mitspeichern — sonst tauchen alte Werte
      // wieder auf, wenn die Auswahl geaendert wurde.
      if (auswahlGesetzt && step.optionalKey && answers[`auswahl.${step.optionalKey}`] !== 'true') continue;
      for (const field of step.fields) {
        if (step.repeatable) {
          // Wiederholte Eintraege liegen als "<stepKey>.<n>.<feldKey>" —
          // ohne diese Schleife gingen alle Kinder/Objekte verloren.
          for (let n = 0; n < step.repeatable.max; n++) put(`${step.key}.${n}.${field.key}`, field);
        } else {
          put(field.key, field);
        }
      }
    }
    // Auswahl selbst mitspeichern, damit sie beim Fortsetzen erhalten bleibt.
    for (const a of catalog.auswahl) {
      payload[`auswahl.${a.key}`] = answers[`auswahl.${a.key}`] === 'true';
    }
    return payload;
  }

  /**
   * Zwischenspeichern ohne den Assistenten zu verlassen.
   *
   * Sicherung gegen Datenverlust: Der Entwurf wird NIE mit einem leeren
   * Antwortsatz ueberschrieben. Sonst genuegte ein Klick auf der Auswahlseite
   * (wo noch keine Antworten existieren), um einen vollstaendigen Entwurf zu
   * loeschen.
   */
  async function saveDraft() {
    if (!catalog) return;
    const payload = buildPayload();
    // Reine Auswahl-Schluessel zaehlen nicht als Inhalt.
    const echteAngaben = Object.keys(payload).filter((k) => !k.startsWith('auswahl.')).length;
    if (echteAngaben === 0) {
      // Nichts erfasst -> auf keinen Fall schreiben. Ein leerer Satz wuerde
      // einen vorhandenen Entwurf ueberschreiben (upsert im Backend).
      setDraftSavedAt(Date.now());
      return;
    }
    setSavingDraft(true);
    setSaveError(null);
    try {
      await taxApi.saveReturn(companyId, 'est', String(year), {
        answers: payload,
        ...vastMeta(),
      });
      setDraftSavedAt(Date.now());
    } catch (e) {
      setSaveError(e);
    } finally {
      setSavingDraft(false);
    }
  }

  async function handleSave() {
    if (!catalog) return;
    setSaveError(null);
    const payload = buildPayload();
    const echteAngaben = Object.keys(payload).filter((k) => !k.startsWith('auswahl.')).length;
    if (echteAngaben === 0) {
      setSaveError(new Error('Es sind noch keine Angaben erfasst — bitte zuerst die Felder ausfüllen.'));
      return;
    }
    setSaving(true);
    try {
      await taxApi.saveReturn(companyId, 'est', String(year), {
        answers: payload,
        ...vastMeta(),
      });
      setSavedPeriod(String(year));
    } catch (e) {
      setSaveError(e);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card className="space-y-3">
        <p className="text-sm text-gray-500">Lade Steuererklärungsassistent …</p>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card className="space-y-3">
        <ErrorNote error={loadError} />
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      </Card>
    );
  }

  if (!catalog || catalog.steps.length === 0) {
    return (
      <Card className="space-y-3">
        <p className="text-sm text-gray-500">Kein Wizard-Katalog verfügbar.</p>
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      </Card>
    );
  }

  // After save: show ElsterSubmit panel.
  if (savedPeriod) {
    return (
      <Card className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Einkommensteuererklärung {year} gespeichert
          </h2>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Deine Angaben wurden gespeichert. Du kannst die Erklärung jetzt prüfen und an das Finanzamt
          übermitteln. Beanstandet ELSTER etwas, gehst du zurück zu den Angaben und ergänzt es –
          deine Eingaben bleiben erhalten.
        </p>
        {/* Rueckweg in den Assistenten: ohne ihn koennte man die von ELSTER
            gemeldeten Punkte nicht abarbeiten, ohne von vorn zu beginnen. */}
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setSavedPeriod(null)}>
            Zurück zu den Angaben
          </Button>
        </div>
        <ElsterSubmit returnType="est" period={savedPeriod} companyId={companyId} />
      </Card>
    );
  }

  // Nur die Schritte, die laut Auswahl relevant sind. Der Pflichtkern (ohne
  // optionalKey) bleibt immer sichtbar.
  const activeSteps = catalog.steps.filter(
    (s) => !s.optionalKey || answers[`auswahl.${s.optionalKey}`] === 'true',
  );

  if (showAuswahl) {
    return (
      <Card className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Einkommensteuererklärung {year}
          </h2>
          <Button variant="ghost" onClick={onClose}>Schließen</Button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Was trifft auf dich zu? Wir zeigen dir dann nur die Schritte, die du wirklich brauchst.
          Persönliche Angaben, Bankverbindung und dein Gewinn kommen immer dran.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {catalog.auswahl.map((a) => {
            const on = answers[`auswahl.${a.key}`] === 'true';
            return (
              <button
                key={a.key}
                onClick={() => setAnswer(`auswahl.${a.key}`, on ? 'false' : 'true')}
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${
                  on
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-950/30'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                }`}
              >
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${
                    on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {on ? '✓' : ''}
                </span>
                <span>
                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{a.label}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">{a.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Du kannst das später jederzeit ändern – nichts geht dabei verloren.
        </p>
        {/* Hier bewusst KEIN "Zwischenspeichern": auf dieser Seite sind noch
            keine Angaben erfasst, ein Speichern haette den Entwurf geleert. */}
        <div className="flex gap-2">
          <Button onClick={() => { setShowAuswahl(false); setStepIdx(0); }}>Weiter</Button>
        </div>
      </Card>
    );
  }

  // Wurde die Auswahl nachtraeglich verkleinert, kann stepIdx ins Leere zeigen.
  const safeIdx = Math.min(stepIdx, Math.max(0, activeSteps.length - 1));
  const currentStep = activeSteps[safeIdx];
  const isLastStep = safeIdx === activeSteps.length - 1;
  if (!currentStep) {
    return (
      <Card className="space-y-3">
        <p className="text-sm text-gray-500">Bitte wähle zuerst aus, was auf dich zutrifft.</p>
        <Button onClick={() => setShowAuswahl(true)}>Zur Auswahl</Button>
      </Card>
    );
  }

  if (!currentStep) {
    return <></>;
  }

  if (showSummary) {
    return (
      <Card className="space-y-4">
        {saveError ? <ErrorNote error={saveError} /> : null}
        <SummaryScreen
          companyId={companyId}
          catalog={catalog}
          answers={answers}
          onBack={() => setShowSummary(false)}
          onSave={handleSave}
          saving={saving}
        />
      </Card>
    );
  }

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Einkommensteuererklärung {year}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Schritt {safeIdx + 1} von {activeSteps.length}
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>
          Abbrechen
        </Button>
      </div>

      <StepProgress steps={activeSteps} current={safeIdx} onJump={setStepIdx} />

      {draftLoaded && safeIdx === 0 && (
        <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-200">
          Gespeicherte Angaben wurden geladen – du kannst dort weitermachen, wo du aufgehört hast.
        </p>
      )}

      {/* Vorausgefüllte Steuererklärung: nur im ersten Schritt anbieten —
          danach sind die Felder bereits ausgefüllt. */}
      {safeIdx === 0 && (
        <VastFetchBox
          companyId={companyId}
          year={catalog.year}
          onUebernehmen={uebernehmeVastWerte}
          gespeichert={vastBelege}
          abrufAm={vastAbrufAm}
          onAbgerufen={(belege) => {
            setVastBelege(belege);
            setVastAbrufAm(new Date().toISOString());
            setDraftSavedAt(null); // Abruf ist erst nach dem Speichern gesichert
          }}
        />
      )}

      <div>
        <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
          {currentStep.title}
        </h3>
        {currentStep.description && (
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">{currentStep.description}</p>
        )}
        {currentStep.anlage && (
          <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">Anlage: {currentStep.anlage}</p>
        )}
      </div>

      <div className="space-y-4">
        {currentStep.repeatable ? (
          <RepeatableStep
            step={currentStep}
            answers={answers}
            setAnswer={setAnswer}
            clearAnswer={clearAnswer}
          />
        ) : currentStep.key === 'hnk35a' ? (
          <Hnk35aStep
            companyId={companyId}
            answers={answers}
            setAnswer={setAnswer}
            fields={currentStep.fields}
          />
        ) : currentStep.key === 'agb' ? (
          <AgbStep
            companyId={companyId}
            answers={answers}
            setAnswer={setAnswer}
            fields={currentStep.fields}
          />
        ) : currentStep.key === 'kapital' ? (
          <KapitalStep
            companyId={companyId}
            answers={answers}
            setAnswer={setAnswer}
            fields={currentStep.fields}
            jahr={year}
          />
        ) : currentStep.key === 'werbungskosten' ? (
          <WerbungskostenStep
            companyId={companyId}
            answers={answers}
            setAnswer={setAnswer}
            fields={currentStep.fields}
            ausBescheinigung={ausBescheinigung}
          />
        ) : (
          currentStep.fields.map((f) => (
            <WizardFieldInput
              key={f.key}
              field={f}
              value={answers[f.key] ?? ''}
              onChange={(v) => setAnswer(f.key, v)}
              answers={answers}
              ausBescheinigung={ausBescheinigung.has(f.key)}
            />
          ))
        )}
      </div>

      <div className="flex gap-2">
        {/* Auf dem ersten Schritt fuehrt "Zurück" zur Auswahl, damit man die
            Anlagen nachtraeglich aendern kann. */}
        <Button
          variant="secondary"
          onClick={() => (safeIdx > 0 ? setStepIdx(safeIdx - 1) : setShowAuswahl(true))}
        >
          Zurück
        </Button>
        {isLastStep ? (
          <Button onClick={() => setShowSummary(true)}>Zusammenfassung</Button>
        ) : (
          <Button onClick={() => setStepIdx(safeIdx + 1)}>Weiter</Button>
        )}
        {/* Zwischenspeichern: ohne das muesste man bis zum letzten Schritt
            durchklicken, um nichts zu verlieren. */}
        <Button variant="ghost" onClick={() => { void saveDraft(); }} busy={savingDraft}>
          {draftSavedAt ? 'Gespeichert ✓' : 'Zwischenspeichern'}
        </Button>
      </div>
    </Card>
  );
}
